import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  carouselsInvalidated,
  selectCarouselRows,
  selectCarouselsFetchedFor,
} from "../../store/homeCarouselsSlice";
import { HOME_CAROUSELS } from "./carouselTypes";
import fetchHomeCarousels, { isSamePlace, locationKeyOf } from "./fetchHomeCarousels";

// How long the first round may wait for the entry location to be decided. Past
// this, fetch with whatever the ladder gives — an unanswered permission dialog
// must not starve the rows. Matches welcome's LOGIN_WAIT_CAP_MS and the loader's
// own cap.
const HOLD_CAP_MS = 8000;

/**
 * Keeps the home carousels in sync with the current coordinates.
 *
 * The fetch itself lives in fetchHomeCarousels so the welcome screen can start
 * the round early; this hook is the home-side guard around it. In the common case
 * welcome has already fetched for these coordinates and the cache check below
 * means home issues nothing at all.
 *
 * The hold below is what makes a fresh entry cost one round instead of three:
 * without it the effect fired on every rung of the location ladder — no location
 * when the token landed, the profile address when Login landed, the GPS fix when
 * the permission prompt was answered — with each newer round discarding the
 * previous response.
 *
 * @param {{
 *   coords: {latitude: number, longitude: number}|null,
 *   source: "gps"|"address"|"profile"|"none",
 *   gpsSettled: boolean,
 * }} params
 * @returns {{rows: Object, allFailed: boolean, retry: Function}}
 */
export default function useHomeCarousels({ coords, source, gpsSettled }) {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userSlice.mainUserData?.Token);
  const id = useSelector((state) => state.userSlice.mainUserData?.ID);
  const phone = useSelector((state) => state.userSlice.mainUserData?.Phone);
  const rows = useSelector(selectCarouselRows);
  const fetchedForLocation = useSelector(selectCarouselsFetchedFor);
  // The same signal waitForLogin uses: the Login response has landed, so the
  // profile address either exists now or never will.
  const loginSettled = useSelector((state) => Object.keys(state.userSlice.userData ?? {}).length > 0);
  const [capExpired, setCapExpired] = useState(false);

  const locationKey = locationKeyOf(coords);

  // The entry location is decided when nothing in flight can still change it:
  // a stored address (rung 1 — a GPS fix or hand-picked address) wins over
  // everything, otherwise both pending sources must have spoken — the entry GPS
  // attempt (settled covers denied/failed/committed) and, when GPS gave nothing,
  // Login (whose profile address is the remaining fallback rung).
  const hasStoredAddress = source === "gps" || source === "address";
  const decided = hasStoredAddress || (gpsSettled && (coords !== null || loginSettled));

  // Clearing fetchedForLocation is what lets the effect re-enter.
  const retry = useCallback(() => dispatch(carouselsInvalidated()), [dispatch]);

  // Arm the cap once credentials are in and the location is still undecided.
  // `decided` only ever goes false → true, so the timer runs from the token
  // landing and is discarded unfired when the decision beats it.
  useEffect(() => {
    if (decided || capExpired || !token) return undefined;
    const timer = setTimeout(() => setCapExpired(true), HOLD_CAP_MS);
    return () => clearTimeout(timer);
  }, [decided, capExpired, token]);

  useEffect(() => {
    if (!token || !id || !phone) return; // credentials not in yet
    // Hold the first round for the decided entry location (or the cap). Later
    // ladder climbs — a post-cap GPS approval, a hand-picked address — pass
    // straight through and revalidate in place below.
    if (!decided && !capExpired) return;
    // Session cache: these coordinates are already fetched (or in flight).
    if (fetchedForLocation === locationKey) return;
    // Close enough to what's fetched that re-ranking would change nothing. The key
    // alone can't see this — a GPS fix and the geocoded address it replaces are
    // almost never within the key's 11m, so every granted-GPS session was paying a
    // second full round for identical rows.
    if (isSamePlace(fetchedForLocation, coords)) return;

    // Rows are only blanked to skeletons when there is nothing to show yet. A
    // location that supersedes an earlier fetch — welcome ran on the saved
    // address, the GPS fix just landed — swaps the rows in place instead.
    fetchHomeCarousels({
      coords,
      mode: fetchedForLocation === null ? "cold" : "revalidate",
      dispatch,
      token,
      id,
      phone,
    });
    // coords is intentionally absent: locationKey is its stable stand-in, and
    // depending on the object would re-fire on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id, phone, locationKey, fetchedForLocation, decided, capExpired, dispatch]);

  const allFailed = HOME_CAROUSELS.every(({ key }) => rows[key]?.status === "error");

  return { rows, allFailed, retry };
}
